export const describeCronExpression = (cronExpression: string, t: (key: string) => string): string => {
    if (!cronExpression || cronExpression.trim() === '') {
        return t('cron.description.invalidExpression');
    }

    const parts = cronExpression.trim().split(/\s+/);

    if (parts.length !== 5) {
        return t('cron.description.invalidExpression');
    }

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    try {
        const description = buildCronDescription(minute, hour, dayOfMonth, month, dayOfWeek, t);
        return description;
    } catch {
        return t('cron.description.invalidExpression');
    }
};

/**
 * Cron ifadesinin parçalarından açıklama oluşturur
 */
const buildCronDescription = (
    minute: string,
    hour: string,
    dayOfMonth: string,
    month: string,
    dayOfWeek: string,
    t: (key: string) => string
): string => {
    // Sıklık belirleme
    if (minute === '*' && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        return t('cron.description.everyMinute');
    }

    if (minute.startsWith('*/')) {
        const interval = minute.substring(2);
        return t('cron.description.everyXMinutes').replace('{{minutes}}', interval);
    }

    if (hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        return t('cron.description.hourlyAt').replace('{{minute}}', formatMinute(minute));
    }

    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        return t('cron.description.dailyAt')
            .replace('{{hour}}', formatHour(hour))
            .replace('{{minute}}', formatMinute(minute));
    }

    if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
        const weekDays = parseWeekDays(dayOfWeek, t);
        return t('cron.description.weeklyAt')
            .replace('{{days}}', weekDays)
            .replace('{{hour}}', formatHour(hour))
            .replace('{{minute}}', formatMinute(minute));
    }

    if (dayOfWeek === '*' && month === '*') {
        const days = parseDayOfMonth(dayOfMonth, t);
        return t('cron.description.monthlyAt')
            .replace('{{days}}', days)
            .replace('{{hour}}', formatHour(hour))
            .replace('{{minute}}', formatMinute(minute));
    }

    if (dayOfWeek === '*') {
        const months = parseMonthNames(month, t);
        const days = parseDayOfMonth(dayOfMonth, t);
        return t('cron.description.yearlyAt')
            .replace('{{months}}', months)
            .replace('{{days}}', days)
            .replace('{{hour}}', formatHour(hour))
            .replace('{{minute}}', formatMinute(minute));
    }

    return t('cron.description.general')
        .replace('{{minute}}', formatMinute(minute))
        .replace('{{hour}}', formatHour(hour))
        .replace('{{dayOfMonth}}', parseDayOfMonth(dayOfMonth, t))
        .replace('{{month}}', parseMonthNames(month, t))
        .replace('{{dayOfWeek}}', parseWeekDays(dayOfWeek, t));
};

const formatMinute = (minute: string): string => {
    if (minute === '*') return '00';
    if (minute === '0') return '00';
    return minute.padStart(2, '0');
};

const formatHour = (hour: string): string => {
    if (hour === '*') return '00';
    return hour.padStart(2, '0');
};

const parseDayOfMonth = (dayOfMonth: string, t: (key: string) => string): string => {
    if (dayOfMonth === '*') return t('cron.description.everyDay');
    if (dayOfMonth.includes(',')) {
        const days = dayOfMonth
            .split(',')
            .map((d) => d.trim())
            .join(', ');
        return t('cron.description.onDays').replace('{{days}}', days);
    }
    if (dayOfMonth.includes('-')) {
        return t('cron.description.betweenDays').replace('{{range}}', dayOfMonth);
    }
    return t('cron.description.onDay').replace('{{day}}', dayOfMonth);
};

const parseWeekDays = (dayOfWeek: string, t: (key: string) => string): string => {
    const weekDayNames = [
        t('general.sunday'),
        t('general.monday'),
        t('general.tuesday'),
        t('general.wednesday'),
        t('general.thursday'),
        t('general.friday'),
        t('general.saturday'),
    ];

    if (dayOfWeek === '*') return t('cron.description.everyDay');

    if (dayOfWeek.includes(',')) {
        const days = dayOfWeek
            .split(',')
            .map((d) => {
                const dayNum = parseInt(d.trim());
                return weekDayNames[dayNum] || d;
            })
            .join(', ');
        return days;
    }

    if (dayOfWeek.includes('-')) {
        const [start, end] = dayOfWeek.split('-').map((d) => parseInt(d.trim()));
        const days = [];
        for (let i = start; i <= end; i++) {
            days.push(weekDayNames[i] || i.toString());
        }
        return days.join(', ');
    }

    const dayNum = parseInt(dayOfWeek);
    return weekDayNames[dayNum] || dayOfWeek;
};

const parseMonthNames = (month: string, t: (key: string) => string): string => {
    const monthNames = [
        '',
        t('general.january'),
        t('general.february'),
        t('general.march'),
        t('general.april'),
        t('general.may'),
        t('general.june'),
        t('general.july'),
        t('general.august'),
        t('general.september'),
        t('general.october'),
        t('general.november'),
        t('general.december'),
    ];

    if (month === '*') return t('cron.description.everyMonth');

    if (month.includes(',')) {
        const months = month
            .split(',')
            .map((m) => {
                const monthNum = parseInt(m.trim());
                return monthNames[monthNum] || m;
            })
            .join(', ');
        return months;
    }

    if (month.includes('-')) {
        const [start, end] = month.split('-').map((m) => parseInt(m.trim()));
        const months = [];
        for (let i = start; i <= end; i++) {
            months.push(monthNames[i] || i.toString());
        }
        return months.join(', ');
    }

    const monthNum = parseInt(month);
    return monthNames[monthNum] || month;
};
